import axios from 'axios';
import logger from '@/utils/logger';
import { clearAdminSession } from '@/utils/adminSession';

const ApiService = {
  init(baseURL) {
    // Remove /api from baseURL as it's already in the routes
    axios.defaults.baseURL = baseURL || `${process.env.VUE_APP_API_URL}`;
    // Add request interceptor to include auth token
    axios.interceptors.request.use(
      (config) => {
        // Check if this is an admin route
        const isAdminRoute = config.url && config.url.includes('/anna-admin/');
        // Check if this is a client manager route
        const isClientManagerRoute = config.url && config.url.includes('/client-manager/');
        const isVendorPortalRoute = config.url && config.url.includes('/vendor-portal/');
        
        if (isAdminRoute) {
          // Use admin token for admin routes
          const adminToken = localStorage.getItem('adminToken');
          if (adminToken) {
            config.headers.Authorization = `Bearer ${adminToken}`;
          }
        } else if (isClientManagerRoute) {
          // Use manager token for client manager routes
          const managerToken = localStorage.getItem('managerToken');
          if (managerToken) {
            config.headers.Authorization = `Bearer ${managerToken}`;
          }
        } else if (isVendorPortalRoute) {
          // Keep vendor portal session separate from client login
          const vendorToken = localStorage.getItem('vendorToken');
          if (vendorToken) {
            config.headers.Authorization = `Bearer ${vendorToken}`;
          }
        } else {
          // Use client token for client routes
          const token = localStorage.getItem('token');
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        // Default Content-Type is application/json (setHeader). Multipart must omit it so the
        // browser sets multipart/form-data with the correct boundary (otherwise multer sees no file).
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
          const h = config.headers;
          if (h) {
            if (typeof h.delete === 'function') {
              h.delete('Content-Type');
              h.delete('content-type');
            } else {
              delete h['Content-Type'];
              delete h['content-type'];
            }
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle auth errors
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle 403 errors (account inactive/pending approval)
        if (error.response && error.response.status === 403) {
          const isPendingApproval = error.response.data?.isPendingApproval;
          const isAdminRoute = error.config && error.config.url && error.config.url.includes('/anna-admin/');
          const isClientManagerRoute = error.config && error.config.url && error.config.url.includes('/client-manager/');
          const isVendorPortalRoute = error.config && error.config.url && error.config.url.includes('/vendor-portal/');
          
          // Handle client account inactive/pending approval
          if (!isAdminRoute && !isClientManagerRoute && !isVendorPortalRoute && isPendingApproval) {
            // Clear client auth data
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            // Get account status (pending or deactivated)
            const accountStatus = error.response.data?.accountStatus || 'pending';
            // Store flag and account status to show approval pending modal on login page
            sessionStorage.setItem('showApprovalPending', 'true');
            sessionStorage.setItem('accountStatus', accountStatus);
            // Redirect to login page if not already there
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
            return Promise.reject(error);
          }
        }
        
        // Handle 401 errors (unauthorized)
        if (error.response && error.response.status === 401) {
          // Check if this is an admin route
          const isAdminRoute = error.config && error.config.url && error.config.url.includes('/anna-admin/');
          // Check if this is a client manager route
          const isClientManagerRoute = error.config && error.config.url && error.config.url.includes('/client-manager/');
          const isVendorPortalRoute = error.config && error.config.url && error.config.url.includes('/vendor-portal/');
          
          if (isAdminRoute) {
            clearAdminSession();
            // Only redirect if not already on login page
            if (window.location.pathname !== '/anna-admin/login' && window.location.pathname !== '/admin/login') {
              window.location.href = '/admin/login?session=expired';
            }
          } else if (isClientManagerRoute) {
            // Handle client manager unauthorized error
            localStorage.removeItem('managerToken');
            localStorage.removeItem('managerUser');
            localStorage.removeItem('managerIsLoggedIn');
            // Only redirect if not already on login page
            if (window.location.pathname !== '/client-manager/login') {
              window.location.href = '/client-manager/login';
            }
          } else if (isVendorPortalRoute) {
            // Do not wipe a fresh login when an optional follow-up call fails
            const url = String(error.config?.url || '')
            const isAuthBootstrap =
              /\/vendor-portal\/(account-status|send-otp|verify-otp|complete-login|password-login)(?:\?|$)/.test(url)
            if (!isAuthBootstrap) {
              localStorage.removeItem('vendorToken')
              localStorage.removeItem('vendorUser')
              if (!window.location.pathname.startsWith('/vendor/login')) {
                window.location.href = '/vendor/login'
              }
            }
          } else {
            // Handle client unauthorized error
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            // Only redirect if not already on login page
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
        }
        return Promise.reject(error);
      }
    );
  },

  setAuthToken(token) {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  },

  setHeader() {
    axios.defaults.headers.common["Content-Type"] = "application/json";
  },

  removeHeader() {
    axios.defaults.headers.common = {};
  },

  get(resource, config = {}) {
    return axios.get(`${resource}`, config);
  },

  post(resource, data, config) {
    return axios.post(`${resource}`, data, config);
  },

  put(resource, data) {
    return axios.put(`${resource}`, data);
  },

  patch(resource, data, config) {
    return axios.patch(`${resource}`, data, config);
  },

  delete(resource) {
    return axios.delete(`${resource}`);
  },

  /**
   * Perform custom Axios request.
   * @param {Object} data
   * @returns {Promise}
   */
  customRequest(data) {
    return axios(data);
  },

  // Company specific endpoints
  async createCompany(data) {
    try {
      const response = await this.post('/company/create', data);
      return response;
    } catch (error) {
      logger.error('API Service - Error creating company:', error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        logger.error('Error response data:', error.response.data);
        logger.error('Error response status:', error.response.status);
        logger.error('Error response headers:', error.response.headers);

        // Format validation errors if they exist
        if (error.response.data && error.response.data.errors) {
          const errorMessages = error.response.data.errors
            .map(err => err.msg || err.message || JSON.stringify(err))
            .join('\n');
          throw new Error(errorMessages);
        }

        // If there's an error message in the response
        if (error.response.data && error.response.data.message) {
          throw new Error(error.response.data.message);
        }
      } else if (error.request) {
        // The request was made but no response was received
        logger.error('Error request:', error.request);
        throw new Error('No response received from server');
      }
      // Something happened in setting up the request that triggered an Error
      throw error;
    }
  },

  updateCompany(id, companyData) {
    return axios.put(`/company/${id}`, companyData);
  },

  getCompany(id) {
    return axios.get(`/company/${id}`);
  },

  async getAllCompanies() {
    try {
      const response = await this.get('/company');
      return response.data;
    } catch (error) {
      logger.error('API Service - Error fetching companies:', error);
      throw error;
    }
  },

  deleteCompany(id) {
    return axios.delete(`/company/${id}`);
  },

  /**
   * Fetch all company accounts for a given companyId
   * @param {string|number} companyId
   * @returns {Promise}
   */
  getCompanyAccounts(companyId) {
    return axios.get(`/company-account/company/${companyId}`);
  },

  /**
   * Create a new service item for a company
   * @param {string|number} companyId
   * @param {Object} data
   * @returns {Promise}
   */
  async createServiceItem(companyId, data) {
    try {
      // Ensure companyId is included in the payload
      const payload = { ...data, companyId };
      const response = await this.post('/customer/service-items', payload);
      return response;
    } catch (error) {
      logger.error('API Service - Error creating service item:', error);
      throw error;
    }
  },

  /**
   * Fetch all service items for a given companyId
   * @param {string|number} companyId
   * @returns {Promise}
   */
  async getAllServiceItems(companyId) {
    try {
      const response = await this.get(`/customer/service-items?companyId=${companyId}`);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error fetching service items:', error);
      throw error;
    }
  },

  /**
   * Create a new non-inventory item for a company
   * @param {string|number} companyId
   * @param {Object} data
   * @returns {Promise}
   */
  async createNonInventoryItem(companyId, data) {
    try {
      const payload = { ...data, companyId };
      const response = await this.post('/customer/non-inventory-items', payload);
      return response;
    } catch (error) {
      logger.error('API Service - Error creating non-inventory item:', error);
      throw error;
    }
  },

  /**
   * Fetch all non-inventory items for a given companyId
   * @param {string|number} companyId
   * @returns {Promise}
   */
  async getAllNonInventoryItems(companyId) {
    try {
      const response = await this.get(`/customer/non-inventory-items?companyId=${companyId}`);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error fetching non-inventory items:', error);
      throw error;
    }
  },

  /**
   * Create a new payment term for a company
   * @param {string|number} companyId
   * @param {Object} data
   * @returns {Promise}
   */
  async createPaymentTerm(companyId, data) {
    try {
      const payload = { ...data, companyId };
      const response = await this.post('/company/payment-terms', payload);
      return response;
    } catch (error) {
      logger.error('API Service - Error creating payment term:', error);
      throw error;
    }
  },

  /**
   * Fetch all payment terms for a given companyId
   * @param {string|number} companyId
   * @returns {Promise}
   */
  async getAllPaymentTerms(companyId) {
    try {
      const response = await this.get(`/company/payment-terms?companyId=${companyId}`);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error fetching payment terms:', error);
      throw error;
    }
  },

  /**
   * Create a new customer prepayment for a company
   * @param {string|number} companyId
   * @param {Object} data
   * @returns {Promise}
   */
  async createCustomerPrepayment(companyId, data) {
    try {
      const payload = { ...data, companyId };
      const response = await this.post('/customer/customer-prepayments', payload);
      return response;
    } catch (error) {
      logger.error('API Service - Error creating customer prepayment:', error);
      throw error;
    }
  },

  /**
   * Fetch a single company account by its ID
   * @param {string|number} accountId
   * @returns {Promise}
   */
  getCompanyAccountById(accountId) {
    return axios.get(`/company-account/${accountId}`);
  },

  /**
   * Fetch all customers for a given companyId (if provided)
   * @param {string|number} [companyId]
   * @returns {Promise<Array>}
   */
  async getAllCustomers(companyId) {
    try {
      const url = companyId ? `/customer/customers?companyId=${companyId}` : '/customer/customers';
      const response = await this.get(url);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error fetching customers:', error);
      throw error;
    }
  },

  /**
   * Anna assistant: search across customer-domain tables (invoices, payments, contacts, consultants, etc.)
   * @param {string|number} companyId
   * @param {string} q
   * @param {string|number} [customerDbId] optional focus customer (numeric DB id)
   */
  /**
   * Assistant composer autocomplete — customers, vendors, consultants, timesheets from DB.
   */
  async fetchAssistantAutocomplete(companyId, q, limit = 8) {
    const params = new URLSearchParams({
      companyId: String(companyId),
      q: String(q || '').trim(),
      limit: String(limit),
    });
    const response = await this.get(`/customer/assistant/autocomplete?${params.toString()}`);
    return response.data;
  },

  async searchCustomerDomain(companyId, q, customerDbId) {
    try {
      const params = new URLSearchParams({
        companyId: String(companyId),
        q: String(q || '').trim(),
      });
      if (customerDbId != null && customerDbId !== '') {
        params.append('customerId', String(customerDbId));
      }
      const response = await this.get(`/customer/assistant/domain-search?${params.toString()}`);
      const payload = response.data;
      return payload?.data ?? payload;
    } catch (error) {
      logger.error('API Service - searchCustomerDomain:', error);
      throw error;
    }
  },

  /**
   * Global mascot/voice assistant chat (OpenAI proxy, JWT). Sends current route context.
   * Voice or text UIs should call this instead of posting to the assistant chat endpoint directly.
   * @param {{ message: string, history?: Array<{ role: string, content: string }>, route?: { fullPath?: string, name?: string|symbol, params?: { id?: string|number } }, companyId?: string|number|null }} payload
   * @returns {Promise<import('axios').AxiosResponse>}
   */
  async postMascotAssistantChat(payload = {}) {
    const message = typeof payload.message === 'string' ? payload.message : '';
    const history = Array.isArray(payload.history) ? payload.history : [];
    const route = payload.route || {};
    const routePath = route.fullPath != null ? String(route.fullPath).slice(0, 800) : '';
    const routeName = route.name != null ? String(route.name).slice(0, 200) : '';
    const companyId =
      payload.companyId != null && payload.companyId !== ''
        ? payload.companyId
        : route.params && route.params.id != null
          ? route.params.id
          : null;
    const body = {
      message,
      history,
      routePath,
      routeName,
      ...(companyId != null && companyId !== '' ? { companyId } : {}),
    };
    return this.post('/customer/assistant/rat-chat', body);
  },

  /**
   * Create a new customer group for a company
   * @param {Object} data - { name, companyId }
   * @returns {Promise}
   */
  async createCustomerGroup(data) {
    try {
      const response = await this.post('/company/customer-groups', data);
      return response;
    } catch (error) {
      logger.error('API Service - Error creating customer group:', error);
      throw error;
    }
  },

  /**
   * Create a new preferred shipping method for a company
   * @param {Object} data - { name, description, companyId }
   * @returns {Promise}
   */
  async createPreferredShippingMethod(data) {
    try {
      const response = await this.post('/vendor/preferred-shipping-methods', data);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error creating preferred shipping method:', error);
      throw error;
    }
  },

  /**
   * Delete a preferred shipping method by id
   * @param {number|string} id
   * @returns {Promise}
   */
  async deletePreferredShippingMethod(id) {
    try {
      return await this.delete(`/vendor/preferred-shipping-methods/${id}`);
    } catch (error) {
      logger.error('API Service - Error deleting preferred shipping method:', error);
      throw error;
    }
  },

  /**
   * Fetch all sales persons for a given companyId
   * @param {string|number} companyId
   * @returns {Promise<Array>}
   */
  async getAllSalesPersons(companyId) {
    try {
      const response = await this.get(`/company/${companyId}/sales-persons`);
      return response.data.salesPersons;
    } catch (error) {
      logger.error('API Service - Error fetching sales persons:', error);
      throw error;
    }
  },

  // Fetch all payment terms for a given companyId
  // Usage: ApiService.get(`/company/payment-terms?companyId=${companyId}`)

  /**
   * Fetch all vendors for a given companyId
   * @param {string|number} companyId
   * @returns {Promise<Array>}
   */
  async getAllVendors(companyId) {
    try {
      const url = companyId ? `/vendor/vendors?companyId=${companyId}` : '/vendor/vendors';
      const response = await this.get(url);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error fetching vendors:', error);
      throw error;
    }
  },

  /**
   * Fetch all bills for a given vendorId
   * @param {string|number} vendorId
   * @returns {Promise}
   */
  async getVendorBills(vendorId) {
    try {
      const response = await this.get(`/vendor/${vendorId}/bills`);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error fetching vendor bills:', error);
      throw error;
    }
  },

  async getBillsByConsultantId(consultantId) {
    try {
      const response = await this.get(`/vendor/enter-bill/consultant/${consultantId}`);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error fetching bills by consultant ID:', error);
      throw error;
    }
  },

  async getConsultantBillsByCompany(companyId) {
    try {
      const response = await this.get(`/vendor/enter-bill/company/${companyId}/consultant-bills`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      logger.error('API Service - Error fetching consultant bills by company:', error);
      return [];
    }
  },

  async getConsultantPayBillPortfolioByCompany(companyId) {
    try {
      const response = await this.get(`/vendor/pay-bill/company/${companyId}/consultant-portfolio`);
      const body = response.data || {};
      return Array.isArray(body.data) ? body.data : [];
    } catch (error) {
      logger.error('API Service - Error fetching consultant pay bill portfolio:', error);
      return [];
    }
  },

  async getEnterBillById(billId) {
    try {
      const response = await this.get(`/vendor/enter-bill/${billId}`);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error fetching enter bill by id:', error);
      throw error;
    }
  },

  /**
   * Bill progress, timesheets, payments, and journals for the transaction summary dialog.
   * @param {string|number} billId
   * @param {string|number} companyId
   * @returns {Promise<Object>}
   */
  async getBillSummary(billId, companyId) {
    try {
      const query = companyId != null && companyId !== '' ? `?companyId=${companyId}` : '';
      const response = await this.get(`/vendor/enter-bill/${billId}/summary${query}`);
      return response.data.data || response.data;
    } catch (error) {
      logger.error('API Service - Error fetching bill summary:', error);
      throw error;
    }
  },

  /**
   * Fetch all bills and expenses for a company
   * @param {string|number} companyId
   * @param {Object} filters - Optional filters: { view, dateFilter, fromDate, toDate, vendorId }
   * @returns {Promise}
   */
  async getBillsAndExpenses(companyId, filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.view) params.append('view', filters.view);
      if (filters.dateFilter) params.append('dateFilter', filters.dateFilter);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.vendorId) params.append('vendorId', filters.vendorId);
      
      const queryString = params.toString();
      const url = `/vendor/enter-bill/company/${companyId}/bills-expenses${queryString ? `?${queryString}` : ''}`;
      const response = await this.get(url);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error fetching bills and expenses:', error);
      throw error;
    }
  },

  /**
   * Verify a vendor-portal Draft bill (Draft → Open, history log; keeps isVendorSubmitted true).
   * @param {string|number} billId
   * @param {string|number} companyId
   */
  async verifyVendorSubmittedBill(billId, companyId) {
    const response = await this.post(`/vendor/enter-bill/${billId}/verify-vendor-submission`, {
      companyId
    });
    return response.data;
  },

  /**
   * Fetch consultants for a given customerId
   * @param {string|number} customerId
   * @returns {Promise<Array>}
   */
  async getConsultantsByCustomer(customerId) {
    try {
      const response = await this.get(`/customer/consultants?customerId=${customerId}`);
      return response.data.data; // Return only the consultants array
    } catch (error) {
      logger.error('API Service - Error fetching consultants:', error);
      throw error;
    }
  },

  /**
   * Fetch all consultants for a given companyId
   * @param {string|number} companyId
   * @returns {Promise<Array>}
   */
  async getAllConsultants(companyId, options = {}) {
    try {
      const params = new URLSearchParams({ companyId: String(companyId) });
      if (options.consultantType && String(options.consultantType).trim() !== '') {
        params.append('consultantType', String(options.consultantType).trim());
      }
      if (options.preferredVendorId != null && String(options.preferredVendorId).trim() !== '') {
        params.append('preferredVendorId', String(options.preferredVendorId).trim());
      }
      const response = await this.get(`/customer/consultants?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      logger.error('API Service - Error fetching consultants:', error);
      throw error;
    }
  },

  /**
   * Consultant counts keyed by preferred vendor id for vendor list cards.
   * @param {string|number} companyId
   * @returns {Promise<Record<string, number>>}
   */
  async getConsultantCountsByVendor(companyId) {
    try {
      const params = new URLSearchParams({ companyId: String(companyId) });
      const response = await this.get(`/customer/consultants/counts-by-vendor?${params.toString()}`);
      return response?.data?.data || {};
    } catch (error) {
      logger.error('API Service - Error fetching consultant counts by vendor:', error);
      throw error;
    }
  },

  /**
   * Load Fulltime / 1099 consultants with timesheets in a pay period for manual payroll.
   * @param {string|number} companyId
   * @param {string} periodStart - YYYY-MM-DD or MM/DD/YYYY
   * @param {string} periodEnd
   * @returns {Promise<{ employees: Array, count: number, periodStart: string, periodEnd: string }>}
   */
  async getManualPayrollTimesheetEmployees(companyId, periodStart, periodEnd) {
    try {
      const params = new URLSearchParams({
        companyId: String(companyId),
        periodStart: String(periodStart || ''),
        periodEnd: String(periodEnd || '')
      });
      const response = await this.get(`/consultant/payroll/manual/timesheet-employees?${params.toString()}`);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error loading payroll timesheet employees:', error);
      throw error;
    }
  },

  /**
   * Load active benefit deductions for one consultant in a pay period (manual payroll mapping).
   */
  async getManualPayrollBenefitDeductions(companyId, consultantId, periodStart, periodEnd, earnings = 0) {
    try {
      const params = new URLSearchParams({
        companyId: String(companyId),
        consultantId: String(consultantId),
        periodStart: String(periodStart || ''),
        periodEnd: String(periodEnd || ''),
        earnings: String(earnings != null ? earnings : 0)
      });
      const response = await this.get(`/consultant/payroll/manual/benefit-deductions?${params.toString()}`);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error loading payroll benefit deductions:', error);
      throw error;
    }
  },

  /**
   * Get consultants for a specific customer
   * @param {string|number} customerId - The customer ID
   * @param {string|number} [companyId] - The company ID (required by backend for filtering)
   * @returns {Promise<Array>}
   */
  async getConsultantsForCustomer(customerId, companyId) {
    try {
      const params = new URLSearchParams({ customerId });
      if (companyId != null && companyId !== '') {
        params.append('companyId', companyId);
      }
      const response = await this.get(`/customer/consultants?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      logger.error('API Service - Error fetching consultants for customer:', error);
      throw error;
    }
  },

  /**
   * Fetch a single consultant by ID
   * @param {string|number} consultantId
   * @returns {Promise<Object>}
   */
  async getConsultantById(consultantId) {
    try {
      const response = await this.get(`/customer/consultants/${consultantId}`);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error fetching consultant by ID:', error);
      throw error;
    }
  },

  /**
   * Update a consultant by ID
   * @param {string|number} consultantId
   * @param {Object} data
   * @returns {Promise<Object>}
   */
  async updateConsultant(consultantId, data) {
    try {
      const response = await this.put(`/customer/consultants/${consultantId}`, data);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error updating consultant:', error);
      throw error;
    }
  },

  /**
   * Fetch consultant addresses by consultant ID
   * @param {string|number} consultantId
   * @returns {Promise<Array>}
   */
  async getConsultantAddresses(consultantId) {
    try {
      const response = await this.get(`/customer/consultant-addresses/${consultantId}`);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error fetching consultant addresses:', error);
      throw error;
    }
  },

  /**
   * Update consultant addresses by consultant ID
   * @param {string|number} consultantId
   * @param {Array} addresses
   * @returns {Promise<Array>}
   */
  async updateConsultantAddresses(consultantId, addresses) {
    try {
      const response = await this.put(`/customer/consultant-addresses/${consultantId}`, addresses);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error updating consultant addresses:', error);
      throw error;
    }
  },

  /**
   * Delete consultant addresses by consultant ID
   * @param {string|number} consultantId
   * @returns {Promise<Object>}
   */
  async deleteConsultantAddresses(consultantId) {
    try {
      const response = await this.delete(`/customer/consultant-addresses/${consultantId}`);
      return response.data;
    } catch (error) {
      logger.error('API Service - Error deleting consultant addresses:', error);
      throw error;
    }
  },

  async getInvoiceItemsByConsultant(consultantId) {
    try {
      const response = await this.get(`/customer/invoice-items/by-consultant/${consultantId}`);
      return response.data.data;
    } catch (error) {
      logger.error('API Service - Error fetching invoice items by consultant:', error);
      throw error;
    }
  },

  async getInvoiceItemsByInvoice(invoiceId) {
    try {
      const response = await this.get(`/customer/invoice-items?invoiceId=${invoiceId}`);
      return response.data.data;
    } catch (error) {
      logger.error('API Service - Error fetching invoice items by invoice:', error);
      throw error;
    }
  },

  async getInvoiceItemsByCompany(companyId) {
    try {
      const response = await this.get(`/customer/invoice-items/by-company?companyId=${companyId}`);
      return response.data.data;
    } catch (error) {
      logger.error('API Service - Error fetching invoice items by company:', error);
      throw error;
    }
  },

  /**
   * Fetch all consultant receive payment records for a specific company
   * @param {string|number} companyId
   * @returns {Promise<{ rows: Array, receiptRecordsByPaymentId: Record<string, Array> }>}
   */
  async getConsultantReceivePaymentRecords(companyId) {
    try {
      const url = companyId ? `/consultant/receive-payment-records?companyId=${companyId}` : '/consultant/receive-payment-records';
      const response = await this.get(url);
      const body = response.data || {};
      const rows = Array.isArray(body.data) ? body.data : (Array.isArray(body) ? body : []);
      const receiptRecordsByPaymentId =
        body.receiptRecordsByPaymentId && typeof body.receiptRecordsByPaymentId === 'object'
          ? body.receiptRecordsByPaymentId
          : {};
      return { rows, receiptRecordsByPaymentId };
    } catch (error) {
      logger.error('API Service - Error fetching consultant receive payment records:', error);
      throw error;
    }
  },

  /**
   * Fetch a single customer invoice by its ID
   * @param {string|number} invoiceId
   * @returns {Promise<Object>}
   */
  async getCustomerInvoiceById(invoiceId) {
    try {
      const response = await this.get(`/customer/invoices/${invoiceId}`);
      return response.data.data;
    } catch (error) {
      logger.error('API Service - Error fetching customer invoice by ID:', error);
      throw error;
    }
  },

  /**
   * Fetch all customer invoices for a given companyId (no backend pagination)
   * @param {string|number} companyId
   * @returns {Promise<Array>}
   */
  async getAllCustomerInvoices(companyId) {
    try {
      const response = await this.get(`/customer/invoices?companyId=${companyId}`);
      return response.data.data;
    } catch (error) {
      logger.error('API Service - Error fetching customer invoices:', error);
      throw error;
    }
  },

  /**
   * Fetch HR timesheets for notification count
   * @param {string|number} companyId
   * @param {string} status - Optional status filter
   * @returns {Promise<Array>}
   */
  async getHRTimesheets(companyId, status = null) {
    try {
      let url = `/timesheets?companyId=${companyId}`;
      if (status) {
        url += `&status=${status}`;
      }
      const response = await this.get(url);
      return response.data.data || [];
    } catch (error) {
      logger.error('API Service - Error fetching HR timesheets:', error);
      return []; // Return empty array instead of throwing error
    }
  },

  /**
   * Fetch comprehensive invoice details including creation logs, journal entries, and timesheets
   * @param {string|number} companyId
   * @param {string|number} invoiceId
   * @returns {Promise<Object>}
   */
  async getInvoiceDetails(companyId, invoiceId) {
    try {
      const response = await this.get(`/customer/invoices/${invoiceId}/details?companyId=${companyId}`);
      return response.data.data || response.data;
    } catch (error) {
      logger.error('API Service - Error fetching invoice details:', error);
      throw error;
    }
  },

  /**
   * Fetch customer invoices for a specific customer
   * @param {string|number} customerId
   * @returns {Promise<Array>}
   */
  async getCustomerInvoices(customerId) {
    try {
      const response = await this.get(`/customer/invoices?customerId=${customerId}`);
      return response.data.data || response.data || [];
    } catch (error) {
      logger.error('API Service - Error fetching customer invoices:', error);
      return []; // Return empty array instead of throwing error
    }
  },

  /**
   * Fetch all vendor pay bills for a given companyId
   * @param {string|number} companyId
   * @returns {Promise<Array>}
   */
  async getVendorPayBillsByCompany(companyId) {
    try {
      const response = await this.get(`/vendor/pay-bill/company/${companyId}`);
      return response.data.data || response.data;
    } catch (error) {
      logger.error('API Service - Error fetching vendor pay bills:', error);
      throw error;
    }
  },

  /** GET /vendor/pay-bill/:payBillId/records — VendorPayBillRecord rows with bill / line detail */
  async getPayBillRecordsByPayBillId(payBillId) {
    try {
      const response = await this.get(`/vendor/pay-bill/${payBillId}/records`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      logger.error('API Service - Error fetching pay bill records:', error);
      throw error;
    }
  },

  /**
   * Fetch tracker data for a company
   * @param {string|number} companyId
   * @param {Object} filters - Optional filters: { fromDate, toDate }
   * @returns {Promise<Array>}
   */
  async getTrackerData(companyId, filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      
      const queryString = params.toString();
      const url = `/customer/tracker/${companyId}${queryString ? `?${queryString}` : ''}`;
      const response = await this.get(url);
      return response.data.data || [];
    } catch (error) {
      logger.error('API Service - Error fetching tracker data:', error);
      throw error;
    }
  },

  /**
   * Batch check email sent today status for multiple invoices
   * @param {Array<number|string>} invoiceIds - Array of invoice IDs
   * @param {string|number} companyId - Company ID
   * @returns {Promise<Object>} - Map of invoiceId -> sentToday boolean
   */
  async batchCheckEmailSentToday(invoiceIds, companyId) {
    try {
      const response = await this.post('/customer/invoices/batch-email-sent-today', {
        invoiceIds: invoiceIds,
        companyId: companyId
      });
      return response.data?.statusMap || {};
    } catch (error) {
      logger.error('API Service - Error batch checking email status:', error);
      throw error;
    }
  }
};

export default ApiService; 